import './date-picker.scss'

import moment from 'moment'
import { FC, useEffect, useState } from 'react'
import { Button, Image } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { calendar_icon, left_icon, right_icon } from 'src/assets'
import { fitem_t } from 'src/types'
import { compare_dates, to_date } from 'src/utils'

import { InputText } from '../input-text/input-text'
import { DAYS, MONTHS } from './date-picker.config'

export const DatePicker_: FC<fitem_t> = ({
  id,
  capture = '',
  disabled_from,
  disabled_to,
  classes,
  ...input_props
}: fitem_t) => {
  /**
   * Use State
   */
  const [current_capt, set_current_capt] = useState(capture)
  const [current_date, set_current_date] = useState(new Date())
  const [days, set_days] = useState<Date[]>()
  const [expanded_calendar, set_expanded_calendar] = useState(false)
  const [warning, set_warning] = useState<string>()

  /**
   * Alias
   */
  const firstday_currmonth = new Date(current_date.getFullYear(), current_date.getMonth(), 1)
  const firstday_nextmonth = new Date(current_date.getFullYear(), current_date.getMonth() + 1, 1)
  const day_of_week = (firstday_currmonth.getDay() + 6) % 7

  /**
   * Use Effect
   */
  useEffect(() => {
    auto_slash()
  }, [capture])

  useEffect(() => {
    update_calendar()
    if (current_capt?.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)) {
      check_effective_date(to_date(current_capt))
    }
  }, [current_capt])

  useEffect(() => {
    const days_ = []
    for (const day = firstday_currmonth; day < firstday_nextmonth; day.setDate(day.getDate() + 1)) {
      days_.push(new Date(day))
    }
    set_days(days_)
  }, [current_date])

  const { t } = useTranslation()

  /**
   * Support functions
   */
  const goto_year = (previuos: boolean) => {
    const year_ = current_date.getFullYear()
    update_current_date(new Date(current_date.setFullYear(previuos ? year_ - 1 : year_ + 1)))
  }

  const goto_month = (previuos: boolean) => {
    const month_ = current_date.getMonth()
    update_current_date(new Date(current_date.setMonth(previuos ? month_ - 1 : month_ + 1)))
  }

  const update_current_date = (new_date: Date) => {
    if (!isNaN(new_date.getTime())) {
      set_current_date(new_date)
    }
  }

  const update_calendar = () => {
    if (current_capt) {
      const str = current_capt
      const res = str?.replaceAll(' ', '')
      const arr = res?.split('/')
      const day = +arr[0]
      let month = +arr[1] || new Date().getMonth()
      month--
      const year = arr[2]?.length === 4 ? +arr[2] : new Date().getFullYear()
      update_current_date(new Date(year, month, day))
    }
  }

  const update_day = (day: Date) => {
    on_change_date(day?.getDate(), day?.getMonth() + 1, day?.getFullYear())
    set_expanded_calendar(false)
    if (id === 'ts1a__effective_date') {
      check_effective_date(day)
    }
  }

  const check_effective_date = (day: Date) => {
    const warning_date = new Date()
    warning_date.setMonth(warning_date.getMonth() + 2)
    const error_date = new Date()
    error_date.setMonth(error_date.getMonth() + 12)
    if (
      compare_dates(day, warning_date) === 1 &&
      (compare_dates(day, error_date) === -1 || compare_dates(day, error_date) === 0)
    ) {
      set_warning(t('ts1.effective_date_alert_2_months'))
    } else if (compare_dates(day, error_date) === 1) {
      set_warning(t('ts1.effective_date_alert_12_months'))
    } else {
      set_warning('')
    }
  }

  const on_change_date = (DD: number, MM: number, YYYY: number) => {
    const DD_ = `${DD}`?.length === 1 ? `0${DD}` : `${DD}`
    const MM_ = `${MM}`?.length === 1 ? `0${MM}` : `${MM}`
    set_current_capt(`${DD_}/${MM_}/${YYYY}`)
    update_calendar()
  }

  const auto_slash = () => {
    const input = capture

    // Rimuovi tutti i non numerici e i separatori
    const cleanedInput = input.replace(/[^\d/]/g, '')

    // Aggiungi automaticamente gli slash dopo il giorno e il mese se non sono già presenti
    let formattedDate = ''
    for (let i = 0; i < cleanedInput.length; i++) {
      formattedDate += cleanedInput[i]
      if (
        (i === 1 || i === 4) &&
        cleanedInput[i + 1] !== '/' &&
        cleanedInput[i] !== '/' &&
        cleanedInput[i - 1] !== '/'
      ) {
        formattedDate += '/'
      }
    }

    // Imposta il risultato
    set_current_capt(formattedDate)
  }

  /**
   * Render functions
   */
  const render_input = () => (
    <InputText
      {...input_props}
      id={id}
      placeholder="DD/MM/YYYY"
      icon={calendar_icon}
      capture={current_capt}
      on_press_icon={() => {
        set_expanded_calendar(!expanded_calendar)
        update_calendar()
      }}
    />
  )

  const render_input_effective_date = () => (
    <InputText
      {...input_props}
      id={id}
      placeholder="DD/MM/YYYY"
      icon={calendar_icon}
      capture={current_capt}
      warning={warning}
      classes_error="calendar-effective-date-error c-error font-12-semibold text-uppercase ml-3"
      on_press_icon={() => {
        set_expanded_calendar(!expanded_calendar)
        update_calendar()
      }}
    />
  )

  const render_selector = (value: string | number, on_press: (previous: boolean) => void) => (
    <div className="p-2 mt-n2 d-flex flex-wrap align-items-baseline justify-content-between">
      <Image src={left_icon} onClick={() => on_press(true)} role="button" />
      <p className="m-0 p-0 font-16-semibold text-uppercase">{value}</p>
      <Image src={right_icon} onClick={() => on_press(false)} role="button" />
    </div>
  )

  const render_days = () => (
    <>
      {/* CALENDAR HEAD */}
      <div className="mt-4 d-flex flex-wrap">
        {DAYS.map((day) => (
          <div key={day} className="calendar-day">
            <small>{day}</small>
          </div>
        ))}
      </div>

      {/* CALENDAR BODY */}
      <div className="d-flex flex-wrap">
        <div style={{ width: `${day_of_week * 14.28}%` }} />
        {days?.map((day) => {
          const highlight = current_capt && compare_dates(day, current_date) === 0

          const f_ = new Date(
            disabled_from ? moment(disabled_from, 'DD/MM/YYYY').format('MM/DD/YYYY') : ''
          )
          const t_ = new Date(
            disabled_to ? moment(disabled_to, 'DD/MM/YYYY').format('MM/DD/YYYY') : ''
          )

          const disabled_f =
            id === 'ts1a__effective_date'
              ? compare_dates(day, f_) === -1
              : compare_dates(day, f_) === 1
          const disabled_t =
            id === 'ts1a__effective_date'
              ? compare_dates(day, t_) === 1
              : compare_dates(day, t_) === -1
          const disabled = disabled_f || disabled_t

          const variant = highlight
            ? 'day-selected c-ffffff'
            : disabled
            ? 'day-disabled c-7f7f7f'
            : ''

          return (
            <div key={day?.getTime()} className="mt-2 calendar-day">
              <Button
                onClick={() => update_day(day)}
                className={'p-0 calendar-day-button rounded-circle'}
                variant={variant}
                disabled={disabled}
              >
                {day.getDate()}
              </Button>
            </div>
          )
        })}
      </div>
    </>
  )

  return (
    <main className={classes}>
      {id === 'ts1a__effective_date' ? render_input_effective_date() : render_input()}
      <div className={`calendar-container shadow ${expanded_calendar ? 'd-block' : 'd-none'}`}>
        <div className="mt-3">
          {render_selector(current_date?.getFullYear(), goto_year)}
          {render_selector(MONTHS?.[current_date?.getMonth()], goto_month)}
        </div>
        {render_days()}
      </div>
    </main>
  )
}
